import { createSlice } from '@reduxjs/toolkit';
import FJConstant from '../../constants/FJConstant';
import {
    SMART_IMAGE_INSTRUCTION_TEMPLATES,
    AI_PHOTO_EDITOR_INSPIRATIONS,
    AI_PHOTO_EDITOR_MODELS,
} from '../../config/dataConfig/FJAIPhotoEditorData';
import FJCommunityUtil from '../../util/FJCommunityUtil';
import { PUBLISH_TYPE } from '../../config/dataConfig/FJCommunityData';

export const AI_PHOTO_EDITOR_INIT_NODES = [
    {
        type: 'paragraph',
        children: [
            {
                text: '',
            },
        ],
    },
];

const defaultModelConfig = AI_PHOTO_EDITOR_MODELS[2];
const initialState = {
    // 基础状态
    uploadStatus: FJConstant.DEFAULT,
    promptStatus: FJConstant.DEFAULT,
    isShowPresetImageBox: false,

    // 图片数据
    originalImage: {},
    inputImages: [], // 多图上传支持

    // 提示词
    promptSlateNodes: AI_PHOTO_EDITOR_INIT_NODES,
    promptMaxLength: defaultModelConfig?.promptCharacterLimit || 5000,
    selectedPreset: null,
    availablePresets: SMART_IMAGE_INSTRUCTION_TEMPLATES,
    randomInspirations: AI_PHOTO_EDITOR_INSPIRATIONS,

    // 模型选择
    curModel: defaultModelConfig?.value || '',
    curRatio: defaultModelConfig?.supportRatios[0] || 'auto',
    curResolution: '',
    // 错误信息
    error: null,

    privacyStatus: FJCommunityUtil.getLocalStoragePrivacyStatus() || PUBLISH_TYPE.ONLY_PUBLIC,

    creationList: [],
    taskStatus: FJConstant.DEFAULT,
    isDone: false, // AI工具已经加工完成并返回了数据
    freeNumCreation: 0,
};

const aiPhotoEditorSlice = createSlice({
    name: 'aiPhotoEditor',
    initialState,
    reducers: {
        setSliceFreeNumCreationAction: (state, action) => {
            state.freeNumCreation = action.payload;
        },
        setSliceCreationListAction: (state, action) => {
            if (!action.payload) return;
            const isLoading = action.payload.filter(i => i.isLoading).length !== 0;
            const isFailed = action.payload.filter(i => i.isReload).length !== 0;
            state.creationList = action.payload;
            state.isDone = action.payload.length > 0 && !isLoading && !isFailed;
        },
        setSliceIsDoneAction: (state, action) => {
            state.isDone = action.payload;
        },
        setSliceTaskStatusAction: (state, action) => {
            state.taskStatus = action.payload;
        },
        // 设置上传状态
        setUploadStatus: (state, action) => {
            state.uploadStatus = action.payload;
        },

        // 设置原始图片
        setOriginalImage: (state, action) => {
            state.originalImage = action.payload;
        },

        // 设置提示词nodes
        setPromptSlateNodes: (state, action) => {
            state.promptSlateNodes = action.payload;
        },

        // 设置选中的预设
        setSelectedPreset: (state, action) => {
            const selectedPreset = action.payload;
            state.selectedPreset = selectedPreset.id;
            state.prompt = selectedPreset.prompt;
        },

        // 重置状态
        resetState: () => {
            return initialState;
        },

        // 设置提示词状态
        setPromptStatus: (state, action) => {
            state.promptStatus = action.payload;
        },

        // 设置当前模型
        setCurModel: (state, action) => {
            state.curModel = action.payload;
        },

        // 设置多图列表
        setInputImages: (state, action) => {
            state.inputImages = action.payload;
        },

        // 添加图片到多图列表
        addInputImage: (state, action) => {
            state.inputImages.push(action.payload);
        },

        // 从多图列表中移除图片
        removeInputImage: (state, action) => {
            const index = action.payload;
            state.inputImages.splice(index, 1);
        },

        // 清空多图列表
        clearInputImages: state => {
            state.inputImages = [];
        },

        // REQUIREMENT: 应用第一张图片，清除其他
        applyFirstInputImage: (state, action) => {
            state.inputImages = [action.payload];
        },
        setSliceRatio: (state, action) => {
            state.curRatio = action.payload;
        },
        setSliceResolution: (state, action) => {
            state.curResolution = action.payload;
        },
        // 设置提示词最大长度
        setPromptMaxLength: (state, action) => {
            state.promptMaxLength = action.payload;
        },
        setSlicePrivacySettingAction: (state, action) => {
            state.privacyStatus = action.payload;
            FJCommunityUtil.setLocalStoragePrivacyStatus(action.payload);
        },
    },
});

export const {
    setUploadStatus,
    setOriginalImage,
    setPromptSlateNodes,
    setSelectedPreset,
    resetState,
    setPromptStatus,
    setCurModel,
    setInputImages,
    addInputImage,
    removeInputImage,
    clearInputImages,
    applyFirstInputImage,
    setSliceRatio,
    setSliceResolution,
    setPromptMaxLength,
    setSlicePrivacySettingAction,
    setSliceCreationListAction,
    setSliceIsDoneAction,
    setSliceTaskStatusAction,
    setSliceFreeNumCreationAction,
} = aiPhotoEditorSlice.actions;

export default aiPhotoEditorSlice.reducer;
